// @ts-strict-ignore
// noinspection JSNonASCIINames,NonAsciiCharacters

import {Component, computed, inject, Input, OnInit, signal} from "@angular/core";
import {Translations} from "@translations";
import {FetchDataType, RoomCapability, TeamTrainingSession, TrainingRoom} from "@models";
import {TeamTrainingSessionStateService} from "../../service/team-training-session-state.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {debounceTime, distinctUntilChanged, filter} from "rxjs/operators";
import {SimEventService, SimMaterialDialogService, NotificationService, SimSessionService} from "@services";
import {FormControl} from "@angular/forms";
import {DialogData_DetailedRoomSelectionOutput} from "../../../../general-dialogs/detailed-room-selection-dialog/detailed-room-selection-dialog.component";

type RoomType = "training" | "debriefing" | "liveStream";

@UntilDestroy()
@Component({
	selector: "sim-team-training-session-step1",
	templateUrl: "./team-training-session-step1.component.html",
	styleUrl: "./team-training-session-step1.component.scss",
	standalone: false,
})
export class TeamTrainingSessionStep1Component implements OnInit {
	// I/O
	@Input() eventEndDate: Date;
	@Input() eventStartDate: Date;

	// Signals
	public displayRooms = signal<Map<RoomType, TrainingRoom[]>>(
		new Map([
			["training", []],
			["debriefing", []],
			["liveStream", []],
		]),
	);
	public typedExcludeRoomIds = signal<Map<RoomType, string[]>>(
		new Map([
			["training", []],
			["debriefing", []],
			["liveStream", []],
		]),
	);
	public roomCount = computed<number>(() => this._computeRoomCount());

	// Readonly
	protected readonly T = Translations;
	protected readonly RoomCapability = RoomCapability;
	protected readonly FetchDataType = FetchDataType;

	// Injections
	private _stateService = inject(TeamTrainingSessionStateService);
	private _sessionService = inject(SimSessionService);
	private _dialogService = inject(SimMaterialDialogService);
	private _eventService = inject(SimEventService);
	private _notificationService = inject(NotificationService);

	// Form
	public step1Form = this._stateService.step1Form;

	public ngOnInit() {
		this._initDisplayRooms();
		this.step1Form.valueChanges
			.pipe(
				untilDestroyed(this),
				distinctUntilChanged(),
				filter(Boolean),
				filter(() => this.step1Form.valid),
				debounceTime(500),
			)
			.subscribe((value) => {
				// Send upsert request
				const savePromise = this._sessionService.upsertTeamTrainingSessionMetaData({
					sessionId: this._stateService.session.id,
					eventId: this._stateService.session.eventId,
					title: value.title,
					startTime: value.date,
					description: "",
					trainingRoomIds: value.trainingRooms.map((room) => room.id),
					debriefingRoomIds: value.debriefingRooms.map((room) => room.id),
					liveStreamRoomIds: value.liveStreamRooms.map((room) => room.id),
				});
				this._stateService.autosavePromise = savePromise;
				savePromise.then((session) => this._updateSessionState(session));
			});
	}

	private _initDisplayRooms(): void {
		if (!this._stateService.session.id) {
			return;
		}
		this.displayRooms.set(
			new Map([
				["training", this._stateService.step1Form.value.trainingRooms],
				["debriefing", this._stateService.step1Form.value.debriefingRooms],
				["liveStream", this._stateService.step1Form.value.liveStreamRooms],
			]),
		);
	}

	protected addRoom(room: TrainingRoom, roomType: RoomType) {
		switch (roomType) {
			case "training":
				this.step1Form.controls.trainingRooms.setValue([...this.step1Form.value.trainingRooms, room]);
				break;
			case "debriefing":
				this.step1Form.controls.debriefingRooms.setValue([...this.step1Form.value.debriefingRooms, room]);
				break;
			case "liveStream":
				this.step1Form.controls.liveStreamRooms.setValue([...this.step1Form.value.liveStreamRooms, room]);
				break;
		}
		this._updateRoomDisplayState();
	}

	protected removeRoom(room: TrainingRoom, roomType: RoomType) {
		switch (roomType) {
			case "training":
				this.step1Form.controls.trainingRooms.setValue(
					this.step1Form.value.trainingRooms.filter((listRoom) => listRoom.id !== room.id),
				);
				break;
			case "debriefing":
				this.step1Form.controls.debriefingRooms.setValue(
					this.step1Form.value.debriefingRooms.filter((listRoom) => listRoom.id !== room.id),
				);
				break;
			case "liveStream":
				this.step1Form.controls.liveStreamRooms.setValue(
					this.step1Form.value.liveStreamRooms.filter((listRoom) => listRoom.id !== room.id),
				);
				break;
		}
		this._updateRoomDisplayState();
	}

	protected openRoomDetailDialog(roomType: RoomType): void {
		const getSelectedRooms = (formControl: FormControl<TrainingRoom[]>, excludeRoomIds: string[]) => {
			const tempRooms = formControl.value;
			this._dialogService
				.openDetailedTrainingRoomSelection(
					{
						preselectedTrainingRooms: tempRooms,
						excludeRoomIds,
						singleRoom: false,
					},
					{width: "1100px", height: "825px"},
				)
				.then((data: DialogData_DetailedRoomSelectionOutput) => {
					if (data.trainingRoomSelection) {
						formControl.setValue(data.trainingRoomSelection);
						this._updateRoomDisplayState();
					}
				});
		};

		switch (roomType) {
			case "training":
				getSelectedRooms(this.step1Form.controls.trainingRooms, [
					...this.typedExcludeRoomIds().get("debriefing"),
					...this.typedExcludeRoomIds().get("liveStream"),
				]);
				break;
			case "debriefing":
				getSelectedRooms(this.step1Form.controls.debriefingRooms, [
					...this.typedExcludeRoomIds().get("training"),
					...this.typedExcludeRoomIds().get("liveStream"),
				]);
				break;
			case "liveStream":
				getSelectedRooms(this.step1Form.controls.liveStreamRooms, [
					...this.typedExcludeRoomIds().get("debriefing"),
					...this.typedExcludeRoomIds().get("training"),
				]);
		}
	}

	private _updateRoomDisplayState(): void {
		const trainingRooms = this.step1Form.value.trainingRooms;
		const debriefingRooms = this.step1Form.value.debriefingRooms;
		const liveStreamRooms = this.step1Form.value.liveStreamRooms;

		// Assign rooms to exclude
		this.typedExcludeRoomIds.set(
			new Map([
				["training", trainingRooms.map((room) => room.id)],
				["debriefing", debriefingRooms.map((room) => room.id)],
				["liveStream", liveStreamRooms.map((room) => room.id)],
			]),
		);

		// Assign rooms to display
		this.displayRooms.set(
			new Map([
				["training", trainingRooms],
				["debriefing", debriefingRooms],
				["liveStream", liveStreamRooms],
			]),
		);
	}

	private _computeRoomCount(): number {
		const trainingLength = this.displayRooms().get("training")?.length ?? 0;
		const debriefingLength = this.displayRooms().get("debriefing")?.length ?? 0;
		const liveStreamLength = this.displayRooms().get("liveStream")?.length ?? 0;
		return trainingLength + debriefingLength + liveStreamLength;
	}

	private _updateSessionState(session: TeamTrainingSession): void {
		const existingId = this._stateService.session.id;
		this._stateService.session = session;
		if (!existingId) {
			this._eventService
				.addSessionToEvent(session.eventId, session.id)
				.catch(() => this._notificationService.notifyError("Error while adding session to event"));
		}
	}
}
